/**
 * Toast context: app-wide non-blocking notifications.
 * Use for task completion (e.g. "Routine update complete").
 * Auto-dismisses after a short duration; placement is responsive
 * (bottom-right on web, top-center on mobile).
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/Toast/Toast';

const ToastContext = createContext();

const DEFAULT_DURATION_MS = 4000;

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showToast = useCallback((message, variant = 'success', durationMs = DEFAULT_DURATION_MS) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const toast = { id, message, variant };

    setToasts((prev) => [...prev, toast]);

    const timer = setTimeout(() => {
      removeToast(id);
    }, durationMs);
    timersRef.current[id] = timer;

    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className="toast-container" aria-label="Notifications">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
